import type { CanvasEngineEntity } from '../entity'
import { Vector2d } from '../types'
import { DIR_DRAW_BEND_ANGLE, DIR_DRAW_MIN_LENGTH, DIR_DRAW_MAX_BENDS } from '../const'

// 타입
export type AlignDirection = 'horizontal' | 'vertical' | 'left' | 'right' | 'top' | 'bottom'

export interface BalloonData {
  shape: CanvasEngineEntity
  pos: Vector2d
  width: number
  height: number
  stroke: number
  centerPoint: Vector2d
}

export interface AlignResult {
  shape: CanvasEngineEntity
  finalPos: Vector2d
}

export interface DirectionData {
  shape: CanvasEngineEntity
  points: number[]
  startX: number
  startY: number
  endX: number
  endY: number
  bend1X: number
  bend1Y: number
  bend2X?: number
  bend2Y?: number
  bendCount: 1 | 2
  isHorizontal: boolean
  isVertical: boolean
  isDiagonal: boolean
  direction: 'left' | 'right' | 'up' | 'down' | null
}

/**
 * ============================================
 * 벌룬 정렬 (Balloon Alignment)
 * ============================================
 *
 * 여러 도형의 번호 풍선을 정렬하는 기능을 제공합니다.
 *
 * 주요 특징:
 * - 6가지 정렬 방향 지원 (left/right/top/bottom/horizontal/vertical)
 * - 가까운 풍선들만 딱 붙임 (먼 풍선은 원래 간격 유지)
 * - 방향도형의 경로 자동 재계산
 * - 삼발이 도형은 정렬에서 제외
 *
 * 정렬 알고리즘:
 * 1. 정렬 방향에 따라 풍선들을 정렬 (위→아래 또는 좌→우)
 * 2. 인접한 풍선들을 그룹화 (간격 임계값 기준)
 * 3. 각 그룹 내에서는 딱 붙이고, 그룹 간에는 원래 간격 유지
 * 4. 방향도형의 경우 풍선 위치까지의 경로 재계산
 */

// 풍선을 붙일지 말지 판단하는 거리 임계값 (픽셀)
// 이 값보다 가까우면 딱 붙이고, 멀면 원래 간격 유지
const BALLOON_GROUP_THRESHOLD = 100

/**
 * 정렬 방향에 따른 다음 위치 계산 (간격 동적 조정)
 * @param currentPos 현재 벌룬 위치
 * @param currentSize 현재 벌룬 크기
 * @param nextSize 다음 벌룬 크기
 * @param alignDirection 정렬 방향
 * @param originalDistance 원래 두 벌룬 간 거리 (옵션)
 * @param reverse 역방향 여부
 * @returns 다음 벌룬의 계산된 위치
 */
function calculateNextPosition(
  currentPos: Vector2d,
  currentSize: { width: number; height: number; stroke: number },
  nextSize: { width: number; height: number; stroke: number },
  alignDirection: AlignDirection,
  originalDistance?: number,
  reverse = false,
): Vector2d {
  const factor = reverse ? -1 : 1
  const currentExtent = {
    width: currentSize.width + currentSize.stroke * 2,
    height: currentSize.height + currentSize.stroke * 2,
  }
  const nextExtent = {
    width: nextSize.width + nextSize.stroke * 2,
    height: nextSize.height + nextSize.stroke * 2,
  }

  switch (alignDirection) {
    case 'left':
    case 'right':
    case 'horizontal': {
      // 세로 정렬: 두 벌룬이 가까우면 딱 붙이고, 멀면 원래 간격 유지
      let spacing = 0
      if (originalDistance !== undefined && originalDistance > BALLOON_GROUP_THRESHOLD) {
        // 먼 벌룬: 원래 간격 유지 (벌룬 크기를 제외한 순수 간격)
        spacing = originalDistance - (currentExtent.height / 2 + nextExtent.height / 2)
      }

      return {
        x: currentPos.x,
        y: currentPos.y + factor * (currentExtent.height / 2 + nextExtent.height / 2 + spacing),
      }
    }
    case 'top':
    case 'bottom':
    case 'vertical': {
      // 가로 정렬: 두 벌룬이 가까우면 딱 붙이고, 멀면 원래 간격 유지
      let spacing = 0
      if (originalDistance !== undefined && originalDistance > BALLOON_GROUP_THRESHOLD) {
        // 먼 벌룬: 원래 간격 유지 (벌룬 크기를 제외한 순수 간격)
        spacing = originalDistance - (currentExtent.width / 2 + nextExtent.width / 2)
      }

      return {
        x: currentPos.x + factor * (currentExtent.width / 2 + nextExtent.width / 2 + spacing),
        y: currentPos.y,
      }
    }
    default:
      return currentPos
  }
}

// 정렬 기준에 따른 중심 위치 계산
function calculateCenterPosition(
  targetValue: number,
  balloon: BalloonData,
  alignDirection: AlignDirection,
): Vector2d {
  const { width, height, stroke, pos } = balloon

  switch (alignDirection) {
    case 'left':
      return { x: targetValue + width / 2 + stroke, y: pos.y }
    case 'right':
      return { x: targetValue - width / 2 - stroke, y: pos.y }
    case 'top':
      return { x: pos.x, y: targetValue + height / 2 + stroke }
    case 'bottom':
      return { x: pos.x, y: targetValue - height / 2 - stroke }
    case 'horizontal':
      return { x: targetValue, y: pos.y }
    case 'vertical':
      return { x: pos.x, y: targetValue }
    default:
      return pos
  }
}

/**
 * 두 벌룬 간 원래 거리 계산
 */
function getOriginalDistance(
  balloon1: BalloonData,
  balloon2: BalloonData,
  alignDirection: AlignDirection,
): number {
  switch (alignDirection) {
    case 'left':
    case 'right':
    case 'horizontal':
      // 세로 방향 거리
      return Math.abs(balloon1.centerPoint.y - balloon2.centerPoint.y)
    case 'top':
    case 'bottom':
    case 'vertical':
      // 가로 방향 거리
      return Math.abs(balloon1.centerPoint.x - balloon2.centerPoint.x)
    default:
      return 0
  }
}

/**
 * 중심 벌룬을 기준으로 양방향으로 배치 (가까운 벌룬은 딱 붙이고, 먼 벌룬은 간격 유지)
 * @param balloons 정렬된 벌룬 데이터 배열
 * @param alignDirection 정렬 방향
 * @param centerPos 중심 벌룬의 목표 위치
 * @param centerIndex 중심 벌룬의 인덱스
 * @param targetValue 정렬 기준값 (라인 위치)
 * @returns 각 벌룬의 최종 위치 배열
 */
function alignBalloonsFromCenter(
  balloons: BalloonData[],
  alignDirection: AlignDirection,
  centerPos: Vector2d,
  centerIndex: number,
  targetValue: number,
): AlignResult[] {
  const results: AlignResult[] = []

  // 중심 벌룬을 기준점에 배치
  results.push({ shape: balloons[centerIndex].shape, finalPos: { ...centerPos } })

  // 중심 벌룬 이후의 벌룬들을 순차 배치 (정방향)
  let currentPos = { ...centerPos }
  for (let i = centerIndex + 1; i < balloons.length; i++) {
    const currentBalloon = balloons[i]
    const prevBalloon = balloons[i - 1]

    // 원래 두 벌룬 간 거리 계산
    const originalDistance = getOriginalDistance(prevBalloon, currentBalloon, alignDirection)

    currentPos = calculateNextPosition(
      currentPos,
      { width: prevBalloon.width, height: prevBalloon.height, stroke: prevBalloon.stroke },
      { width: currentBalloon.width, height: currentBalloon.height, stroke: currentBalloon.stroke },
      alignDirection,
      originalDistance,
    )

    const correctedPos = correctAlignmentPosition(
      currentPos,
      currentBalloon,
      alignDirection,
      targetValue,
    )
    results.push({ shape: currentBalloon.shape, finalPos: correctedPos })
  }

  // 중심 벌룬 이전의 벌룬들을 역순 배치 (역방향)
  currentPos = { ...centerPos }
  for (let i = centerIndex - 1; i >= 0; i--) {
    const currentBalloon = balloons[i]
    const nextBalloon = balloons[i + 1]

    // 원래 두 벌룬 간 거리 계산
    const originalDistance = getOriginalDistance(currentBalloon, nextBalloon, alignDirection)

    currentPos = calculateNextPosition(
      currentPos,
      { width: nextBalloon.width, height: nextBalloon.height, stroke: nextBalloon.stroke },
      { width: currentBalloon.width, height: currentBalloon.height, stroke: currentBalloon.stroke },
      alignDirection,
      originalDistance,
      true, // reverse
    )

    const correctedPos = correctAlignmentPosition(
      currentPos,
      currentBalloon,
      alignDirection,
      targetValue,
    )
    results.push({ shape: currentBalloon.shape, finalPos: correctedPos })
  }

  return results
}

// 정렬 방향에 따라 벌룬 위치를 정렬축 기준으로 보정
function correctAlignmentPosition(
  pos: Vector2d,
  balloon: BalloonData,
  alignDirection: AlignDirection,
  targetValue: number,
): Vector2d {
  const { width, height, stroke } = balloon

  switch (alignDirection) {
    case 'left':
      // 벌룬의 왼쪽 끝이 targetValue에 맞춰지도록
      return { x: targetValue + width / 2 + stroke, y: pos.y }
    case 'right':
      // 벌룬의 오른쪽 끝이 targetValue에 맞춰지도록
      return { x: targetValue - width / 2 - stroke, y: pos.y }
    case 'top':
      // 벌룬의 위쪽 끝이 targetValue에 맞춰지도록
      return { x: pos.x, y: targetValue + height / 2 + stroke }
    case 'bottom':
      // 벌룬의 아래쪽 끝이 targetValue에 맞춰지도록
      return { x: pos.x, y: targetValue - height / 2 - stroke }
    case 'horizontal':
    case 'vertical':
      // 중심선 정렬은 보정 없음
      return pos
    default:
      return pos
  }
}

/**
 * 벌룬 정렬 순서 결정 - 현재 위치 기반으로 정렬하여 이동 최소화
 */
function sortBalloonsByDirection(
  balloons: BalloonData[],
  alignDirection: AlignDirection,
): BalloonData[] {
  return [...balloons].sort((a, b) => {
    switch (alignDirection) {
      case 'left':
      case 'right':
      case 'horizontal':
        // 세로 정렬: Y 좌표 기준 (위에서 아래)
        return a.centerPoint.y - b.centerPoint.y
      case 'top':
      case 'bottom':
      case 'vertical':
        // 가로 정렬: X 좌표 기준 (좌에서 우)
        return a.centerPoint.x - b.centerPoint.x
      default:
        return 0
    }
  })
}

/**
 * 정렬 라인에 가장 가까운 벌룬 찾기
 */
function findClosestBalloonToTarget(
  balloons: BalloonData[],
  targetValue: number,
  alignDirection: AlignDirection,
): number {
  let closestIndex = 0
  let minDistance = Infinity

  balloons.forEach((balloon, index) => {
    let distance: number
    switch (alignDirection) {
      case 'left':
      case 'right':
      case 'horizontal':
        distance = Math.abs(balloon.pos.x - targetValue)
        break
      case 'top':
      case 'bottom':
      case 'vertical':
        distance = Math.abs(balloon.pos.y - targetValue)
        break
      default:
        distance = Infinity
    }

    if (distance < minDistance) {
      minDistance = distance
      closestIndex = index
    }
  })

  return closestIndex
}

/**
 * 벌룬들을 정렬하여 배치 - 정렬 라인에 가장 가까운 벌룬을 중심으로 배치
 */
export function alignBalloonsInOrder(
  balloons: BalloonData[],
  targetValue: number,
  alignDirection: AlignDirection,
): AlignResult[] {
  if (balloons.length === 0) return []

  const sortedBalloons = sortBalloonsByDirection(balloons, alignDirection)

  // 정렬 라인에 가장 가까운 벌룬을 중심으로 선택 (이동 최소화)
  const centerIndex = findClosestBalloonToTarget(sortedBalloons, targetValue, alignDirection)
  const centerBalloon = sortedBalloons[centerIndex]

  const centerPos = calculateCenterPosition(targetValue, centerBalloon, alignDirection)

  return alignBalloonsFromCenter(
    sortedBalloons,
    alignDirection,
    centerPos,
    centerIndex,
    targetValue,
  )
}

/**
 * 정렬 기준값 계산
 * - left/right/top/bottom: 해당 방향의 극단값
 * - horizontal/vertical: 양끝 경계의 중간점 (직관적인 중앙 정렬)
 */
export function calculateAlignTargetValue(
  balloons: BalloonData[],
  alignDirection: AlignDirection,
): number {
  switch (alignDirection) {
    case 'left':
      // 가장 왼쪽 벌룬의 왼쪽 끝
      return Math.min(...balloons.map(b => b.pos.x - b.width / 2 - b.stroke))
    case 'right':
      // 가장 오른쪽 벌룬의 오른쪽 끝
      return Math.max(...balloons.map(b => b.pos.x + b.width / 2 + b.stroke))
    case 'top':
      // 가장 위쪽 벌룬의 위쪽 끝
      return Math.min(...balloons.map(b => b.pos.y - b.height / 2 - b.stroke))
    case 'bottom':
      // 가장 아래쪽 벌룬의 아래쪽 끝
      return Math.max(...balloons.map(b => b.pos.y + b.height / 2 + b.stroke))
    case 'horizontal': {
      // 가로 중앙: 가장 왼쪽 끝과 가장 오른쪽 끝의 중간점
      const leftMost = Math.min(...balloons.map(b => b.pos.x - b.width / 2 - b.stroke))
      const rightMost = Math.max(...balloons.map(b => b.pos.x + b.width / 2 + b.stroke))
      return (leftMost + rightMost) / 2
    }
    case 'vertical': {
      // 세로 중앙: 가장 위쪽 끝과 가장 아래쪽 끝의 중간점
      const topMost = Math.min(...balloons.map(b => b.pos.y - b.height / 2 - b.stroke))
      const bottomMost = Math.max(...balloons.map(b => b.pos.y + b.height / 2 + b.stroke))
      return (topMost + bottomMost) / 2
    }
    default:
      return 0
  }
}

/**
 * 방향도형 벌룬 위치까지의 최적 경로 탐색
 * @param pathCoords 현재 경로 좌표
 * @param targetPoint 목표 지점 (벌룬 위치)
 * @param flexible 유연한 탐색 모드 (정렬 시 true)
 * @returns 새로운 경로 좌표 배열 또는 false
 */
export function findDirectionPathToBalloon(
  pathCoords: number[],
  targetPoint: Vector2d,
  flexible = false,
): number[] | false {
  if (pathCoords.length < 4) return false

  const [startX, startY, nextX, nextY] = pathCoords
  const rawInitialDirection = Math.atan2(nextY - startY, nextX - startX)
  const initialDirection = roundToDegreesAndRadians(rawInitialDirection)
  const maxDistanceToTarget = Math.sqrt(
    (targetPoint.x - startX) ** 2 + (targetPoint.y - startY) ** 2,
  )

  // DIR_DRAW_BEND_ANGLE을 라디안으로 변환
  const bendAngleRad = (DIR_DRAW_BEND_ANGLE * Math.PI) / 180

  // 유연한 모드에서는 허용 오차를 증가시킴
  const angleTolerance = flexible ? 0.01 : 1e-10

  // 각도 유틸리티 함수들
  function roundToDegreesAndRadians(angleRad: number): number {
    const degrees = (angleRad * 180) / Math.PI
    const roundedDegrees = Math.round(degrees)
    return (roundedDegrees * Math.PI) / 180
  }

  function normalizeAngle(angle: number): number {
    while (angle > Math.PI) angle -= 2 * Math.PI
    while (angle < -Math.PI) angle += 2 * Math.PI
    return angle
  }

  function getAngleDifference(angle1: number, angle2: number): number {
    const norm1 = normalizeAngle(angle1)
    const norm2 = normalizeAngle(angle2)
    let diff = Math.abs(norm1 - norm2)
    if (diff > Math.PI) diff = 2 * Math.PI - diff
    return diff
  }

  function isTargetOnLine(fromX: number, fromY: number, direction: number): boolean {
    const rawTargetDirection = Math.atan2(targetPoint.y - fromY, targetPoint.x - fromX)
    const targetDirection = roundToDegreesAndRadians(rawTargetDirection)
    const angleDiff = getAngleDifference(direction, targetDirection)
    return angleDiff < angleTolerance
  }

  function getBendDirections(currentDirection: number): number[] {
    const leftDir = roundToDegreesAndRadians(currentDirection - bendAngleRad)
    const rightDir = roundToDegreesAndRadians(currentDirection + bendAngleRad)
    return [leftDir, rightDir]
  }

  function getOrderedDirections(fromX: number, fromY: number, directions: number[]): number[] {
    const targetDir = Math.atan2(targetPoint.y - fromY, targetPoint.x - fromX)
    return directions.sort((a, b) => {
      const aDiff = getAngleDifference(a, targetDir)
      const bDiff = getAngleDifference(b, targetDir)
      return aDiff - bDiff
    })
  }

  // 경로 탐색 설정 - 유연한 모드에서는 탐색 범위 확대
  const maxLength = flexible
    ? Math.min(maxDistanceToTarget * 4, 4000)
    : Math.min(maxDistanceToTarget * 3, 3000)
  const stepSize = flexible
    ? Math.max(1, DIR_DRAW_MIN_LENGTH / 16) // 더 큰 스텝으로 빠르게 탐색
    : Math.max(0.5, DIR_DRAW_MIN_LENGTH / 32)

  // 1선분 직접 도달 확인
  if (isTargetOnLine(startX, startY, initialDirection)) {
    return [startX, startY, targetPoint.x, targetPoint.y]
  }

  // 2선분 (1선분으로 부터 파생된 선분)
  for (let len1 = DIR_DRAW_MIN_LENGTH; len1 <= maxLength; len1 += stepSize) {
    const point1X = startX + Math.cos(initialDirection) * len1
    const point1Y = startY + Math.sin(initialDirection) * len1
    const bendDirections = getBendDirections(initialDirection)

    for (const direction of getOrderedDirections(point1X, point1Y, bendDirections)) {
      if (isTargetOnLine(point1X, point1Y, direction)) {
        return [startX, startY, point1X, point1Y, targetPoint.x, targetPoint.y]
      }
    }
  }

  // 3선분 (2선분으로부터..)
  for (let len1 = DIR_DRAW_MIN_LENGTH; len1 <= maxLength; len1 += stepSize) {
    const point1X = startX + Math.cos(initialDirection) * len1
    const point1Y = startY + Math.sin(initialDirection) * len1
    const bendDirections1 = getBendDirections(initialDirection)

    for (const direction1 of bendDirections1) {
      const maxLen2 = Math.min(
        Math.sqrt((targetPoint.x - point1X) ** 2 + (targetPoint.y - point1Y) ** 2) * 2,
        2000,
      )
      const minLen2 = Math.max(DIR_DRAW_MIN_LENGTH / 8, 1)

      for (let len2 = minLen2; len2 <= maxLen2; len2 += stepSize) {
        const point2X = point1X + Math.cos(direction1) * len2
        const point2Y = point1Y + Math.sin(direction1) * len2
        const bendDirections2 = getBendDirections(direction1)

        for (const direction2 of getOrderedDirections(point2X, point2Y, bendDirections2)) {
          if (isTargetOnLine(point2X, point2Y, direction2)) {
            return [
              startX,
              startY,
              point1X,
              point1Y,
              point2X,
              point2Y,
              targetPoint.x,
              targetPoint.y,
            ]
          }
        }
      }
    }
  }

  // 4선분
  if (DIR_DRAW_MAX_BENDS >= 3) {
    const step4 = Math.max(stepSize * 1.5, 1) // 4선분은 약간 큰 단위로
    for (let len1 = DIR_DRAW_MIN_LENGTH; len1 <= maxLength; len1 += step4) {
      const point1X = startX + Math.cos(initialDirection) * len1
      const point1Y = startY + Math.sin(initialDirection) * len1
      const bendDirections1 = getBendDirections(initialDirection)

      for (const direction1 of bendDirections1) {
        const maxLen2 = Math.min(
          Math.sqrt((targetPoint.x - point1X) ** 2 + (targetPoint.y - point1Y) ** 2) * 1.8,
          1800,
        )
        const minLen2 = Math.max(DIR_DRAW_MIN_LENGTH / 8, 1)

        for (let len2 = minLen2; len2 <= maxLen2; len2 += step4) {
          const point2X = point1X + Math.cos(direction1) * len2
          const point2Y = point1Y + Math.sin(direction1) * len2
          const bendDirections2 = getBendDirections(direction1)

          for (const direction2 of bendDirections2) {
            const maxLen3 = Math.min(
              Math.sqrt((targetPoint.x - point2X) ** 2 + (targetPoint.y - point2Y) ** 2) * 1.5,
              1500,
            )
            const minLen3 = Math.max(DIR_DRAW_MIN_LENGTH / 8, 1)

            for (let len3 = minLen3; len3 <= maxLen3; len3 += step4) {
              const point3X = point2X + Math.cos(direction2) * len3
              const point3Y = point2Y + Math.sin(direction2) * len3
              const bendDirections3 = getBendDirections(direction2)

              for (const direction3 of getOrderedDirections(point3X, point3Y, bendDirections3)) {
                if (isTargetOnLine(point3X, point3Y, direction3)) {
                  return [
                    startX,
                    startY,
                    point1X,
                    point1Y,
                    point2X,
                    point2Y,
                    point3X,
                    point3Y,
                    targetPoint.x,
                    targetPoint.y,
                  ]
                }
              }
            }
          }
        }
      }
    }
  }

  return false
}

/**
 * ============================================
 * 꺾은선 정렬 (Direction Shape Alignment)
 * ============================================
 *
 * 방향도형(꺾은선)의 꺾은점들을 정렬하는 기능을 제공합니다.
 *
 * 주요 특징:
 * - 꺾은점이 2개인 도형만 정렬 가능
 * - 수직/수평/대각선 도형 모두 지원
 * - 도형의 방향성과 각도를 유지하면서 정렬
 * - 정렬 불가능한 도형은 자동 필터링
 *
 * 정렬 방식:
 * - 모든 도형의 꺾은점 중 가장 극단값을 기준점으로 선택
 * - 각 도형의 원래 방향과 각도를 유지하면서 이동
 * - 부적절한 방향의 도형은 정렬에서 제외
 */
// 도형 데이터 변환
function createDirectionDataFromShape(shape: CanvasEngineEntity): DirectionData | null {
  if (!shape.isDirection()) return null

  const points = shape.getPoints()

  // 꺾은점 1개(6) 또는 2개(8)인 경우만 처리
  if (points.length !== 6 && points.length !== 8) return null

  const bendCount = points.length === 6 ? 1 : 2
  const startX = points[0]
  const startY = points[1]
  const bend1X = points[2]
  const bend1Y = points[3]

  let bend2X: number | undefined
  let bend2Y: number | undefined
  let endX: number
  let endY: number

  if (bendCount === 1) {
    // 꺾은점 1개: [startX, startY, bend1X, bend1Y, endX, endY]
    endX = points[4]
    endY = points[5]
  } else {
    // 꺾은점 2개: [startX, startY, bend1X, bend1Y, bend2X, bend2Y, endX, endY]
    bend2X = points[4]
    bend2Y = points[5]
    endX = points[6]
    endY = points[7]
  }

  const deltaX = Math.abs(bend1X - startX)
  const deltaY = Math.abs(bend1Y - startY)
  const totalDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)

  if (totalDistance < 5) return null

  // 수직/수평 판단
  const isHorizontal = deltaY < 1 && deltaX > 5
  const isVertical = deltaX < 1 && deltaY > 5

  // 대각선 판단
  const isDiagonal = !isHorizontal && !isVertical

  // 수직/수평/대각선 중 하나는 반드시 해당해야 함
  if (!isHorizontal && !isVertical && !isDiagonal) return null

  // 방향 계산 (수직/수평 도형만)
  let direction: 'left' | 'right' | 'up' | 'down' | null = null

  if (isHorizontal) {
    direction = bend1X > startX ? 'right' : 'left'
  } else if (isVertical) {
    direction = bend1Y > startY ? 'down' : 'up'
  }

  return {
    shape,
    points,
    startX,
    startY,
    endX,
    endY,
    bend1X,
    bend1Y,
    bend2X,
    bend2Y,
    bendCount: bendCount as 1 | 2,
    isHorizontal,
    isVertical,
    isDiagonal,
    direction,
  }
}

// 이동할 목적기준점 계산
function calculateUnifiedAlignReference(
  allDirectionData: DirectionData[],
  alignDirection: AlignDirection,
): number {
  const allBendPoints: number[] = []

  allDirectionData.forEach(data => {
    if (alignDirection === 'left' || alignDirection === 'right') {
      // X 축 정렬: 모든 꺾은점의 X 좌표 수집
      allBendPoints.push(data.bend1X)
      if (data.bendCount === 2 && data.bend2X !== undefined) {
        allBendPoints.push(data.bend2X)
      }
    } else {
      // Y 축 정렬: 모든 꺾은점의 Y 좌표 수집
      allBendPoints.push(data.bend1Y)
      if (data.bendCount === 2 && data.bend2Y !== undefined) {
        allBendPoints.push(data.bend2Y)
      }
    }
  })

  if (allBendPoints.length === 0) return 0

  switch (alignDirection) {
    case 'left':
    case 'top':
      return Math.min(...allBendPoints)
    case 'right':
    case 'bottom':
      return Math.max(...allBendPoints)
    default:
      return 0
  }
}

// 수직/수평 도형 필터링
function filterValidOrthogonalShapes(
  directionData: DirectionData[],
  alignDirection: AlignDirection,
  referenceCoord: number,
): DirectionData[] {
  return directionData.filter(data => {
    // 수직/수평 도형만 대상
    if (!data.isHorizontal && !data.isVertical) return false

    // 정렬 방향과 도형 방향 일치 여부 확인
    if (data.isHorizontal && (alignDirection === 'top' || alignDirection === 'bottom')) {
      return false
    }
    if (data.isVertical && (alignDirection === 'left' || alignDirection === 'right')) {
      return false
    }

    // 방향성 제약 확인
    if (data.isHorizontal) {
      if (alignDirection === 'left' && data.direction === 'right') {
        const bothPointsLeftOfReference = data.startX < referenceCoord && data.endX < referenceCoord
        return bothPointsLeftOfReference
      }
      if (alignDirection === 'right' && data.direction === 'left') {
        const bothPointsRightOfReference =
          data.startX > referenceCoord && data.endX > referenceCoord
        return bothPointsRightOfReference
      }
    }

    if (data.isVertical) {
      if (alignDirection === 'top' && data.direction === 'down') {
        const bothPointsAboveReference = data.startY < referenceCoord && data.endY < referenceCoord
        return bothPointsAboveReference
      }
      if (alignDirection === 'bottom' && data.direction === 'up') {
        const bothPointsBelowReference = data.startY > referenceCoord && data.endY > referenceCoord
        return bothPointsBelowReference
      }
    }

    return true
  })
}

// 대각선 도형 필터링
function filterValidDiagonalShapes(
  directionData: DirectionData[],
  alignDirection: AlignDirection,
  referenceCoord: number,
): DirectionData[] {
  return directionData.filter(data => {
    // 대각선 도형만 대상
    if (!data.isDiagonal) return false

    // 대각선 도형은 더 관대한 조건으로 정렬 대상 포함
    // 꺾은점이 기준점과 다른 위치에 있으면 정렬 대상으로 포함
    let shouldInclude = false

    switch (alignDirection) {
      case 'left':
        // 꺾은점이 기준점보다 오른쪽에 있으면 왼쪽으로 정렬 가능
        shouldInclude = data.bend1X > referenceCoord
        if (data.bendCount === 2 && data.bend2X !== undefined) {
          shouldInclude = shouldInclude || data.bend2X > referenceCoord
        }
        break
      case 'right':
        // 꺾은점이 기준점보다 왼쪽에 있으면 오른쪽으로 정렬 가능
        shouldInclude = data.bend1X < referenceCoord
        if (data.bendCount === 2 && data.bend2X !== undefined) {
          shouldInclude = shouldInclude || data.bend2X < referenceCoord
        }
        break
      case 'top':
        // 꺾은점이 기준점보다 아래쪽에 있으면 위쪽으로 정렬 가능
        shouldInclude = data.bend1Y > referenceCoord
        if (data.bendCount === 2 && data.bend2Y !== undefined) {
          shouldInclude = shouldInclude || data.bend2Y > referenceCoord
        }
        break
      case 'bottom':
        // 꺾은점이 기준점보다 위쪽에 있으면 아래쪽으로 정렬 가능
        shouldInclude = data.bend1Y < referenceCoord
        if (data.bendCount === 2 && data.bend2Y !== undefined) {
          shouldInclude = shouldInclude || data.bend2Y < referenceCoord
        }
        break
      default:
        return false
    }

    return shouldInclude
  })
}

// 모든 방향도형 정렬 실행
function alignAllDirectionShapes(
  directionData: DirectionData[],
  alignDirection: AlignDirection,
  referenceCoord: number,
): {
  shape: CanvasEngineEntity
  newPoints: number[]
}[] {
  const results: {
    shape: CanvasEngineEntity
    newPoints: number[]
  }[] = []

  for (const data of directionData) {
    const newPoints = [...data.points]

    const bend1X = data.bend1X
    const bend1Y = data.bend1Y
    const bend2X = data.bend2X!
    const bend2Y = data.bend2Y!

    // 시작점 → 첫 번째 꺾은점의 각도 계산 (기존 방향성)
    const deltaX = bend1X - data.startX
    const deltaY = bend1Y - data.startY
    const angle = Math.atan2(deltaY, deltaX) // 시작점에서 첫 번째 꺾은점으로의 각도

    // 정렬 방향에 따라 기준 꺾은점 선택
    let primaryBendX: number, primaryBendY: number

    switch (alignDirection) {
      case 'left':
        // 더 왼쪽 꺾은점을 기준으로 정렬
        if (bend1X < bend2X) {
          primaryBendX = bend1X
          primaryBendY = bend1Y
        } else {
          primaryBendX = bend2X
          primaryBendY = bend2Y
        }
        break

      case 'right':
        // 더 오른쪽 꺾은점을 기준으로 정렬
        if (bend1X > bend2X) {
          primaryBendX = bend1X
          primaryBendY = bend1Y
        } else {
          primaryBendX = bend2X
          primaryBendY = bend2Y
        }
        break

      case 'top':
        // 더 위쪽 꺾은점을 기준으로 정렬
        if (bend1Y < bend2Y) {
          primaryBendX = bend1X
          primaryBendY = bend1Y
        } else {
          primaryBendX = bend2X
          primaryBendY = bend2Y
        }
        break

      case 'bottom':
        // 더 아래쪽 꺾은점을 기준으로 정렬
        if (bend1Y > bend2Y) {
          primaryBendX = bend1X
          primaryBendY = bend1Y
        } else {
          primaryBendX = bend2X
          primaryBendY = bend2Y
        }
        break

      default:
        continue
    }

    // 각도 보존하며 이동량 계산
    let moveX = 0
    let moveY = 0

    switch (alignDirection) {
      case 'left':
      case 'right':
        moveX = referenceCoord - primaryBendX
        moveY = moveX * Math.tan(angle)
        break

      case 'top':
      case 'bottom':
        moveY = referenceCoord - primaryBendY
        if (Math.abs(Math.tan(angle)) > 0.001) {
          moveX = moveY / Math.tan(angle)
        } else {
          moveX = 0
        }
        break
    }

    // 모든 꺾은점에 동일한 벡터 적용
    newPoints[2] = bend1X + moveX // bend1X
    newPoints[3] = bend1Y + moveY // bend1Y
    newPoints[4] = bend2X + moveX // bend2X
    newPoints[5] = bend2Y + moveY // bend2Y

    // 변경사항이 있는지 확인
    const hasChanged = data.points.some(
      (val: number, index: number) => Math.abs(val - newPoints[index]) > 0.1,
    )

    if (hasChanged) {
      results.push({ shape: data.shape, newPoints })
    }
  }

  return results
}

// 방향도형 정렬
export function alignDirectionShapes(
  shapes: CanvasEngineEntity[],
  alignDirection: AlignDirection,
): {
  shape: CanvasEngineEntity
  newPoints: number[]
}[] {
  // 방향도형 데이터 변환
  const allDirectionData = shapes
    .map(createDirectionDataFromShape)
    .filter((data): data is DirectionData => data !== null)

  if (allDirectionData.length === 0) return []

  // 통합 기준점 계산
  const unifiedReferenceCoord = calculateUnifiedAlignReference(allDirectionData, alignDirection)

  const results: {
    shape: CanvasEngineEntity
    newPoints: number[]
  }[] = []

  // 꺾은점 2개인 도형만 정렬 처리
  const all2BendData = allDirectionData.filter(data => data.bendCount === 2)
  if (all2BendData.length > 0) {
    const validOrthogonalData = filterValidOrthogonalShapes(
      all2BendData.filter(data => data.isHorizontal || data.isVertical),
      alignDirection,
      unifiedReferenceCoord,
    )

    const validDiagonalData = filterValidDiagonalShapes(
      all2BendData.filter(data => data.isDiagonal),
      alignDirection,
      unifiedReferenceCoord,
    )

    // 모든 유효한 도형을 통합 로직으로 정렬
    const allValidData = [...validOrthogonalData, ...validDiagonalData]
    if (allValidData.length > 0) {
      const allResults = alignAllDirectionShapes(
        allValidData,
        alignDirection,
        unifiedReferenceCoord,
      )
      results.push(...allResults)
    }
  }

  return results
}
// 방향도형 정렬 끝
